import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { isAfter, isWeekend } from 'date-fns'
import { CircleCheck } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import { cn } from '@fortem-monorepo/js-utilities'
import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'

import { SchemaType } from '@/components/forms/SendEmailsToClientsFormNew/SendEmailsToClientsFormNew'

export interface SendEmailsToClientsFormFieldsProps {}

export const SendEmailsToClientsFormFields: React.FC<SendEmailsToClientsFormFieldsProps> = ({}) => {
  const { control, formState, setValue } = useFormContext<SchemaType>()

  const api = useApi()

  const query = useQuery({
    queryKey: ['client-pricing', 'clients', 'new'],
    queryFn: () => api.clientPricing.v2.getClients(),
  })

  const clients = useMemo(() => {
    return query.data ?? []
  }, [query.data])

  return (
    <div className={'flex flex-col gap-4 w-full'}>
      <FormField
        name="value_date"
        control={control}
        render={({ field: { value } }) => {
          return (
            <FormItem>
              <FormLabel>Value Date</FormLabel>
              <DatePicker
                buttonDisabled={formState.isSubmitting}
                selected={value ? new Date(value) : undefined}
                onSelect={(value) =>
                  value && setValue('value_date', toISO8601(value), { shouldValidate: true })
                }
                disabled={(val) => isWeekend(val) || isAfter(val, new Date())}
              />
            </FormItem>
          )
        }}
      />
      <FormField
        control={control}
        name="org_ids"
        render={({ field: { value } }) => {
          return (
            <FormItem>
              <div className="mb-4 flex items-center gap-2">
                <FormLabel>Clients</FormLabel>
                <Button
                  type="button"
                  size="sm"
                  variant={'outline'}
                  onClick={() => {
                    if (value.length === clients.length) {
                      setValue('org_ids', [], { shouldValidate: true })
                      return
                    }
                    setValue(
                      'org_ids',
                      clients.map((client) => client.org_id),
                      { shouldValidate: true },
                    )
                  }}
                >
                  Toggle All
                </Button>
              </div>
              <div className={'grid gap-2 grid-cols-2'}>
                {clients.map((client) => {
                  const checked = value.includes(client.org_id)
                  return (
                    <div
                      key={client.org_id}
                      onClick={() => {
                        if (checked) {
                          const filtered = [...value].filter((v) => v !== client.org_id)
                          setValue('org_ids', filtered, { shouldValidate: true })
                          return
                        }
                        setValue('org_ids', [...value, client.org_id], {
                          shouldValidate: true,
                        })
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 py-2 px-3 border rounded-md text-sm transition-all bg-gray-50 hover:cursor-pointer whitespace-nowrap',
                        {
                          'pointer-events-none opacity-50': formState.isSubmitting,
                          ['bg-green-50']: checked,
                          ['hover:bg-gray-100']: !checked,
                        },
                      )}
                    >
                      <CircleCheck
                        strokeWidth={1.5}
                        className={cn(
                          'transition-all [&>path]:scale-100 ease-in-out duration-200',
                          {
                            ['text-green-600']: checked,
                            ['text-gray-300']: !checked,
                            ['[&>path]:scale-0']: !checked,
                          },
                        )}
                      />
                      {client.org_display_name}
                    </div>
                  )
                })}
              </div>
            </FormItem>
          )
        }}
      />
    </div>
  )
}
